import React, { useState, useRef, useEffect } from "react";
import * as Portal from "@radix-ui/react-portal";
import { AnimatePresence, motion } from "framer-motion";
import { Container } from "../Container";
import { useCoverageEditorContext } from "../CoverageEditor";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useMultiPetDiscountCopy } from "@/shared/contexts/MultiPetDiscount";
import PetDropdown from "./MultiPetSelectDropdown/PetDropdown";
import { useMultiPetSelectContext } from ".";
import { Button } from "../ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDog, faCat, faChevronDown, faPlus, faPaw, faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { Species } from "@spotpetinsurance/spot-types/entities/Species";
import PetModal from "../PetModal";
import Strings from "@/shared/utils/Strings.constants";
import { UIUtils } from "@/shared/utils/UIUtils";
import { ADD_PET_MODAL_TRIGGERS, CHECKOUT_CTA_VARIANTS } from "@/shared/utils/constants";
import StickyPrice from "../StickyPrice";
import { Badge } from "@/shared/components/ui";
import { cn } from "@/shared/utils";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { StickyCheckoutCtaButton } from "../CoverageEditor/CheckoutCta";
import { Option, Policy } from "@/shared/types/Quote.interface";
import { PartnerOfferConfig } from "@/shared/types/Features";

type StickyMenuProps = {
    isVisible: boolean;
    showStickyPriceLabel?: boolean;
};

type MobileStickyLabelProps = {
    trailingIcon: IconDefinition;
    totalLabel: string;
    isBadgeVisible?: boolean;
    activePet: Policy | undefined;
    policyPets: Option[];
    partnerOffer?: PartnerOfferConfig;
    finalPrice: number;
    beforePrice: number;
    billingFreqText: string;
};

const MobileStickyLabel = ({ trailingIcon, totalLabel, isBadgeVisible, activePet, policyPets, partnerOffer, finalPrice, beforePrice, billingFreqText }: MobileStickyLabelProps) => (
    <div data-testid="sticky-price-label-mobile" className="flex min-w-0 grow items-center justify-between gap-2 sm:hidden">
        <div className="flex min-w-0 items-start gap-2">
            <FontAwesomeIcon icon={isBadgeVisible ? faPaw : activePet?.species === Species.Dog ? faDog : faCat} className="mt-2 size-5 shrink-0" />
            <div className="flex min-w-0 flex-col leading-tight">
                {isBadgeVisible ? (
                    <span data-testid="policy-count-mobile" className="inline-block max-w-[120px] truncate text-left text-sm font-semibold">
                        {policyPets.length} Pets
                    </span>
                ) : (
                    <span className="inline-block max-w-[120px] truncate text-left text-sm font-semibold">{activePet?.name ?? "Select a pet"}</span>
                )}
                <div className="flex flex-row gap-1">
                    <span
                        className={cn("text-xs font-medium text-content-brand-b-secondary sm:text-sm dark:text-content-secondary", partnerOffer && "text-content-success-invert")}
                    >
                        {totalLabel}
                    </span>
                    <StickyPrice totalPrice={finalPrice} preDiscountPrice={beforePrice} billingFreqText={billingFreqText} />
                </div>
            </div>
        </div>
        <FontAwesomeIcon icon={trailingIcon} className="size-4 shrink-0" />
    </div>
);

export const StickyMenu = ({ isVisible, showStickyPriceLabel }: StickyMenuProps) => {
    const [shouldRender, setShouldRender] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const editorConfig = useCoverageEditorContext();
    const { getModalInitialValues, formId } = editorConfig;
    const { appState, updateAppState } = useAppLayerContext();
    const multiPetCopy = useMultiPetDiscountCopy();
    const { isMultiPetLinked, quoteQuery, addPetModal, partnerOffer, checkoutCtaVariant, isCheckoutCtaVisible } = appState;
    const quote = quoteQuery?.data;
    const policies = quote?.policies;
    const { policyPets, handleActivePetChange, petModalConfig, activePet } = useMultiPetSelectContext();
    const defaultCoverageSettings = policies?.[0]?.coverageSettings;
    const calculatedFrequency = appState?.billingFrequency;
    const currentFrequency = quote?.billingInfo?.frequency;
    const freq = calculatedFrequency ?? currentFrequency;
    const billingFreqText = freq === "yearly" ? "yr" : "mo";
    const priceData = appState.calculatedPriceData;

    let finalPrice = 0;
    let beforePrice = 0;

    if (priceData?.finalPrice !== undefined) {
        // Use the aggregate price data
        finalPrice = priceData.finalPrice;
        beforePrice = priceData.preDiscountPrice ?? finalPrice;
    } else {
        // Fallback: sum all policies
        finalPrice =
            policies?.reduce((sum, p) => {
                const base = p.basePrice?.value ?? 0;
                const disc = p.discountAmount?.value ?? 0;
                return sum + (base - disc);
            }, 0) ?? 0;

        beforePrice = policies?.reduce((sum, p) => sum + (p.basePrice?.value ?? 0), 0) ?? 0;
    }

    const label = showStickyPriceLabel === true;
    const isMultiPet = policyPets.length > 1;
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldRender(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    const isBadgeVisible = isMultiPetLinked && policyPets.length > 1;

    const handleAddPet = () => {
        updateAppState({ addPetModal: ADD_PET_MODAL_TRIGGERS.STICKY_MENU });
    };

    const handleCloseModal = () => {
        updateAppState({ addPetModal: undefined });
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        handleAddPet();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleAddPet();
        }
    };

    const handleSmoothPetChange = (petId: string) => {
        handleActivePetChange(petId);

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    // ENG-17301 — variant 1 only; `isCheckoutCtaVisible` is the scroll window published by CoverageEditor.
    const isStickyCheckoutVariant = checkoutCtaVariant === CHECKOUT_CTA_VARIANTS.STICKY_MENU;
    const showStickyCheckoutCta = isStickyCheckoutVariant && !!isCheckoutCtaVisible;

    const trailingIcon = isBadgeVisible ? faCircleInfo : faChevronDown;
    const totalLabel = isBadgeVisible || isMultiPet ? "Tot:" : "Total:";

    const dropdownTrigger = (
        <Button
            type="button"
            variant="invert"
            className={cn(
                "flex h-11 items-center justify-between rounded-md px-2 text-content-frame sm:w-fit sm:px-2 dark:bg-background-transparent dark:hover:bg-background-frame-secondary",
                !isBadgeVisible && "w-full max-w-[180px] text-base sm:max-w-[240px]",
                label && isDropdownOpen && "dark:bg-background-frame-secondary"
            )}
            // Button's inner flex wrapper won't shrink below its content without min-w-0, so the pet name never truncates and the label overflows the trigger.
            decoratorWrapperClass="min-w-0 w-full"
            data-testid={`pet-dropdown-trigger-${isBadgeVisible ? "linked" : "unlinked"}`}
        >
            {/* sm and up */}
            <div className={cn("items-center gap-2", label ? "hidden sm:flex" : "flex")}>
                {isBadgeVisible ? (
                    <>
                        <FontAwesomeIcon icon={faPaw} className="size-5" />
                        <span data-testid="policy-count">{policyPets.length} Pets</span>
                        <FontAwesomeIcon icon={faCircleInfo} className="size-5" />
                    </>
                ) : (
                    <>
                        <FontAwesomeIcon icon={activePet?.species === Species.Dog ? faDog : faCat} className="size-5 shrink-0" />
                        <span className="max-w-[125px] truncate text-sm">{activePet?.name ?? "Select a pet"}</span>
                        <FontAwesomeIcon icon={faChevronDown} className="shrink-0" />
                    </>
                )}
            </div>

            {/* xs only */}
            {label && (
                <MobileStickyLabel
                    trailingIcon={trailingIcon}
                    totalLabel={totalLabel}
                    isBadgeVisible={isBadgeVisible}
                    activePet={activePet}
                    policyPets={policyPets}
                    partnerOffer={partnerOffer}
                    finalPrice={finalPrice}
                    beforePrice={beforePrice}
                    billingFreqText={billingFreqText}
                />
            )}
        </Button>
    );

    const petModalTrigger = (
        <Button
            loading={quoteQuery?.isLoading}
            // ENG-17301 variant 1: while faded out behind the checkout CTA this button is invisible,
            // so it must not stay tabbable or clickable.
            disabled={showStickyCheckoutCta}
            tabIndex={showStickyCheckoutCta ? -1 : undefined}
            loadingSpinnerPosition="start"
            onClick={handleAddPet}
            onTouchStart={handleTouchStart}
            onKeyDown={handleKeyDown}
            variant="invert"
            size="sm"
            className={cn("flex h-11 w-min shrink-0 items-center justify-between whitespace-nowrap rounded-md px-2 py-3 text-xs text-content-frame dark:bg-background-transparent")}
            startDecorator={<FontAwesomeIcon icon={faPlus} className={cn("size-4 text-content-frame")} />}
        >
            {multiPetCopy.stickyCta === multiPetCopy.stickyCtaCompact ? (
                multiPetCopy.stickyCta
            ) : (
                // Only when the wording differs, so the DOM never holds the same label twice for text locators to trip on.
                <>
                    <span className="xs:hidden">{multiPetCopy.stickyCtaCompact}</span>
                    <span className="hidden xs:inline">{multiPetCopy.stickyCta}</span>
                </>
            )}
        </Button>
    );

    return (
        <Portal.Root>
            <AnimatePresence>
                {isVisible && shouldRender && (
                    <>
                        <motion.div initial={{ y: -120 }} animate={{ y: 0 }} exit={{ y: -120 }} transition={{ duration: 0.2 }} className="fixed inset-x-0 top-0 z-[25] w-full">
                            <div ref={dropdownRef} className="bg-background-frame py-2 shadow-sm">
                                <Container>
                                    <div className="-mx-2 flex items-center justify-between gap-2">
                                        <div className="min-w-0 grow">
                                            <PetDropdown
                                                policyPets={policyPets}
                                                handleActivePetChange={handleSmoothPetChange}
                                                petModalConfig={petModalConfig}
                                                triggerButton={dropdownTrigger}
                                                isMultiPetLinked={isMultiPetLinked}
                                                containerRef={dropdownRef}
                                                onOpenChange={setIsDropdownOpen}
                                            />
                                        </div>

                                        {label && (
                                            <div data-testid="sticky-price-label-desktop" className="hidden sm:block">
                                                <Badge className={cn("bg-background-brand-b-secondary", partnerOffer && "bg-background-success")}>
                                                    <StickyPrice totalPrice={finalPrice} preDiscountPrice={beforePrice} billingFreqText={billingFreqText} />
                                                </Badge>
                                            </div>
                                        )}

                                        {/* ENG-17301 variant 1: the checkout CTA takes over this slot while the user is
                                            between the coverage card and the step's footer CTA.

                                            Both buttons are stacked in one grid cell rather than absolutely positioned:
                                            the cell then sizes itself to the wider of the two and centres both on the
                                            same axis as the price badge, so the swap can't paint over the pet name and
                                            no width is hardcoded. Both stay mounted so the slot never resizes mid-scroll
                                            and PetModal keeps its controlled open state; the faded-out one is taken out
                                            of the tab order and the a11y tree (which also keeps `getByRole` unambiguous
                                            against the footer CTA's identical label). */}
                                        {(isStickyCheckoutVariant || !petModalConfig.hideTrigger) && (
                                            <div className={cn("shrink-0", isStickyCheckoutVariant && "grid items-center justify-items-end")}>
                                                {isStickyCheckoutVariant && (
                                                    <motion.div
                                                        data-testid="sticky-menu-checkout-cta-anim"
                                                        // Both wrappers sit in the same grid cell, so the faded-out one still
                                                        // covers the other and would swallow its clicks — `disabled` only takes
                                                        // the inner button out of the pointer path, never its wrapper.
                                                        className={cn("col-start-1 row-start-1", !showStickyCheckoutCta && "pointer-events-none")}
                                                        initial={false}
                                                        // Slides in right-to-left as it fades up, and back out left-to-right.
                                                        animate={{ opacity: showStickyCheckoutCta ? 1 : 0, x: showStickyCheckoutCta ? 0 : 24 }}
                                                        transition={{ duration: 0.2, ease: "easeOut" }}
                                                        aria-hidden={!showStickyCheckoutCta}
                                                    >
                                                        <StickyCheckoutCtaButton formId={formId} isHidden={!showStickyCheckoutCta} />
                                                    </motion.div>
                                                )}
                                                {!petModalConfig.hideTrigger && (
                                                    <motion.div
                                                        data-testid="sticky-menu-add-a-pet-anim"
                                                        className={cn(isStickyCheckoutVariant && "col-start-1 row-start-1", showStickyCheckoutCta && "pointer-events-none")}
                                                        initial={false}
                                                        // Cross-fades against the checkout pill above.
                                                        animate={{ opacity: showStickyCheckoutCta ? 0 : 1 }}
                                                        transition={{ duration: 0.2, ease: "easeOut" }}
                                                        // Focus is removed via `disabled` on the trigger itself (see
                                                        // petModalTrigger), which is what makes this aria-hidden safe.
                                                        aria-hidden={showStickyCheckoutCta}
                                                    >
                                                        <PetModal
                                                            initialValues={{ policies: [getModalInitialValues(defaultCoverageSettings, !!isMultiPetLinked)] }}
                                                            triggerButton={petModalTrigger}
                                                            isOpen={addPetModal === ADD_PET_MODAL_TRIGGERS.STICKY_MENU}
                                                            handleOpen={handleAddPet}
                                                            onClose={handleCloseModal}
                                                            config={petModalConfig}
                                                            handleActivePetChange={handleSmoothPetChange}
                                                        />
                                                    </motion.div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Container>
                            </div>
                            {partnerOffer && (
                                <Container>
                                    {/* Align the savings badge under the pet-count dropdown from sm and up */}
                                    <div className="flex justify-center sm:justify-start">
                                        <div
                                            data-testid="sticky-menu-discount-savings"
                                            className="rounded-md rounded-t-none bg-background-success px-2 py-1 text-xs font-medium leading-4 text-content-success shadow-sm"
                                        >
                                            {Strings.DISCOUNT_SAVINGS} <strong>{UIUtils.formatCurrency(Math.abs(beforePrice - finalPrice))}</strong>
                                        </div>
                                    </div>
                                </Container>
                            )}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </Portal.Root>
    );
};
